import S, { DataSignal } from 's-js';

import { Measurement, NullableMeasurement } from 'data/models/Measurement';
import { PortCall, VesselIdentifier } from 'data/models/PortCall';
import { ScheduleDateType } from './ScheduleDateType';

import { PilotJobOrder as ServerPilotJobOrder, PilotJobOrderOptions } from 'server/models/PilotJobOrder';

export { PilotJobOrderData, PilotJobOrderOptions } from 'server/models/PilotJobOrder';

export class PilotJobOrder extends ServerPilotJobOrder {
    EffectiveLength : EffectiveLength;
    EffectiveAirDraft  : EffectiveDraft;
    EffectiveDeepDraft : EffectiveDraft;

    constructor();
    constructor(data : PilotJobOrderOptions);
    constructor(data : PilotJobOrderOptions | null = null) {
        super(data || { Id: 0 });
        this.EffectiveLength    = EffectiveLength   (this);
        this.EffectiveAirDraft  = EffectiveAirDraft (this);
        this.EffectiveDeepDraft = EffectiveDeepDraft(this);
    }

    StartEntity() {
        return this.Schedule().find(d => d.Type() === ScheduleDateType.PJOStart) || null;
    }

    EndEntity() {
        return this.Schedule().find(d => d.Type() === ScheduleDateType.PJOEnd) || null;
    }

    EffectivePortCall() {
        return this.Cancellation() ? this.Cancellation()!.PointInTime() && this.Cancellation()!.PointInTime()!.PortCall() : this.PortCall();
    }

    EffectiveFromPoint() {
        return this.Cancellation() ? this.Cancellation()!.PointInTime() && this.Cancellation()!.PointInTime()!.FromPoint() : this.FromPoint();
    }

    EffectiveToPoint() {
        return this.Cancellation() ? this.Cancellation()!.PointInTime() && this.Cancellation()!.PointInTime()!.ToPoint() : this.ToPoint();
    }

    DisplayVesselIdentifier() {
        return VesselIdentifier(
            () => this.EffectiveVessel(),
            () => this.EffectivePortCall() && this.EffectivePortCall()!.Agent(),
            new Measurement({
                Num: this.EffectiveLength.Num(),
                UOM: this.EffectiveLength.UOM()
            })
        );
    }

    EffectiveVessel() {
        // TH 03/10/2017 -- Use Length.Num to test if the Vessel has been "locked".
        // We use Length.Num instead of testing VesselId b/c in theory the Vessel could've been
        // locked at a time when the PortCall Vessel was still null.
        return this.Length.Num() !== null ? this.Vessel() : this.EffectivePortCall() ? this.EffectivePortCall()!.Vessel() : null;
    }

    ApprovalOfCharges() {
        return this._approvalsOfCharges().length === 0 ? null : this._approvalsOfCharges()[0];
    }
}

export interface EffectiveLength {
    Num : DataSignal<number>;
    UOM : () => string;
}

export function EffectiveLength(pjo : PilotJobOrder) : EffectiveLength {
    const effective = () =>
        pjo.Length.Num() !== null ? pjo.Length as Measurement :
        pjo.EffectivePortCall()?.VesselLength ?? new Measurement();

    return {
        Num: (val? : number) => {
            if (val === undefined) return effective().Num() ?? 0;
            pjo.Length.set(new Measurement({ Num: val, UOM: S.sample(() => effective().UOM() ?? '' )}));
            return val;
        },
        UOM: () => effective().UOM() ?? ''
    };
}

export interface EffectiveDraft {
    Num : DataSignal<number | null>;
    UOM : () => string | null;
    Lock : () => void;
}

export function EffectiveDraft(
    pjo : PilotJobOrder,
    pjoDraft : NullableMeasurement,
    pcDraft : (pc : PortCall) => NullableMeasurement
) : EffectiveDraft {
    const effective = () =>
        // TH 02/03/2017 -- If pjoDraft is non-null or someone has set DeepDraftConfirmed,
        // then the pjoDraft takes precedence...
        pjoDraft.Num() !== null || pjo.DeepDraftConfirmed() ? pjoDraft :
        // ...else if PortCall is populated, fall through to PortCall draft...
        pjo.EffectivePortCall() ? pcDraft(pjo.EffectivePortCall()!) :
        // ...else use a dummy measurement as a final fallback.
        new NullableMeasurement();

    return {
        Num: (val? : number | null) => {
            if (val === undefined) return effective().Num();
            pjoDraft.set(new NullableMeasurement({ Num: val, UOM: S.sample(() => effective().UOM() )}));
            return val;
        },
        UOM: () => effective().UOM(),

        // TH 02/03/2017 -- To be called when setting DeepDraftConfirmed, to persist the
        // current effective value.
        Lock: () => pjoDraft.set(effective())
    };
}

export function EffectiveAirDraft(pjo : PilotJobOrder) {
    return EffectiveDraft(pjo, pjo.AirDraft, pc => pc.AirDraft);
}

export function EffectiveDeepDraft(pjo : PilotJobOrder) {
    return EffectiveDraft(pjo, pjo.DeepDraft, pc => pc.DeepDraft);
}


// WEBPACK FOOTER //
// ./data/models/PilotJobOrder.ts